import { config } from '@config/config';
import { REFRESH_TOKEN_MUTATION } from '@graphql/mutations/session/refreshTokenMutation';
import type { RefreshTokenResponse } from '@service/auth';
import { isTokenExpired } from '@shared/utils/tokenUtils';

import { useCookiesManager } from '../useCookiesManager';

/**
 * Force refresh the access token regardless of expiry status
 * This is useful after actions that change user state (like creating an organization)
 * where the token needs to be updated with new claims
 */
export const forceRefreshToken = async (): Promise<string | null> => {
  try {
    const refreshToken = useCookiesManager.getRefreshToken();

    if (!refreshToken) {
      return null;
    }

    const response = await fetch(config.API_GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: REFRESH_TOKEN_MUTATION.loc?.source.body || '',
        variables: {
          input: {
            refreshToken,
          },
        },
      }),
    });

    if (!response.ok) {
      return null;
    }

    const result: { data: { refreshToken: RefreshTokenResponse } } = await response.json();

    if (result.data?.refreshToken?.accessToken && result.data?.refreshToken?.refreshToken) {
      const { accessToken, refreshToken: newRefreshToken } = result.data.refreshToken;

      useCookiesManager.setTokens(accessToken, newRefreshToken);

      return accessToken;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Check if a JWT token is expired by decoding it locally
 * This avoids unnecessary network calls
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  const accessToken = useCookiesManager.getAccessToken();

  if (!accessToken) {
    return null;
  }

  const tokenExpired = await isTokenExpired(accessToken);

  if (!tokenExpired) {
    return useCookiesManager.getAccessToken();
  }

  return forceRefreshToken();
};
